import { useMemo, useState } from 'react';

import {
  Box,
  ChartContainer,
  ChartGrid,
  ChartTooltip,
  ChartTooltipTimestampHeader,
  Line,
  Stack,
  TimeXAxis,
  YAxis,
} from '@cast/design-system';
import { compactNumber, dashesOrNum, toNumOrUndefined } from '@cast/utils';
import { useCluster } from '@cast/shared-cluster-context-data-access';
import { FailedToLoad } from '@cast/shared-messages-ui';
import {
  buildAnalyzePrompt,
  useAnalyzeWithOpsPilot,
} from '@cast/features-organization-shared-opspilot';
import {
  ReliabilitySegmentDot,
  buildPairedGapEdges,
  ReliabilityDatapoint,
} from '@cast/split-me/features/cluster/_components/reliability';
import { useWorkloadReliabilityData } from '@cast/split-me/features/cluster/workload-optimization';

import { WidgetLayout } from '../../../_components/ui/WidgetLayout';
import { useInventoryWorkloadsTimeRangeSelection } from '../../../_hooks/useInventoryWorkloadsTimeRangeSelection';
import { isRangeSupported } from '../../_utils/isRangeSupported';
import { DateRangeUnsupportedBanner } from '../_components/DateRangeUnsupportedBanner';
import { ReliabilityMetricsSummary } from './_components/ReliabilityMetricsSummary';

const trafficTickFormatter = (value: number | string): string => {
  const numericValue = toNumOrUndefined(value);
  return numericValue === undefined
    ? String(value)
    : compactNumber(numericValue, { maximumFractionDigits: 2 });
};

export const ReliabilityTrafficChart = () => {
  const { timeZone, getRange, preset, key, from, to } =
    useInventoryWorkloadsTimeRangeSelection();
  const timeRangeSelection = {
    getRange,
    preset,
    timeZone,
    key,
  };
  const { cluster } = useCluster();
  const {
    data,
    chartData,
    trafficMetrics,
    isLoading,
    isFetching,
    showNoData,
    error,
    refetch,
  } = useWorkloadReliabilityData({ timeRangeSelection });

  const hasError = !!error;
  const rangeSupported = isRangeSupported(from, to);

  const [visibleMetrics, setVisibleMetrics] = useState<Record<string, boolean>>(
    () => Object.fromEntries(trafficMetrics.map((m) => [m.key, true]))
  );

  const toggleMetric = (key: string): void => {
    setVisibleMetrics((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const visibleMetricsList = trafficMetrics.filter(
    (m) => visibleMetrics[m.key]
  );

  const pairedGapEdges = useMemo(
    () =>
      Object.fromEntries(
        trafficMetrics.map((m) => [
          m.key,
          buildPairedGapEdges(chartData, m.key),
        ])
      ),
    [chartData, trafficMetrics]
  );

  const analyzeWithOpsPilot = useAnalyzeWithOpsPilot({
    buildPrompt: buildAnalyzePrompt({
      context: 'Traffic reliability metrics',
      scopeName: cluster.name,
      getDefaultRange: getRange,
    }),
  });

  if (!rangeSupported) {
    return (
      <WidgetLayout
        title="Reliability traffic"
        testId="workload-reliability-traffic-chart"
      >
        <DateRangeUnsupportedBanner testId="workload-reliability-traffic-chart-unsupported-range" />
      </WidgetLayout>
    );
  }

  return (
    <WidgetLayout
      title="Reliability traffic"
      testId="workload-reliability-traffic-chart"
    >
      {hasError && !isFetching ? (
        <FailedToLoad
          title="Failed to load reliability traffic"
          refresh={refetch}
        />
      ) : (
        <Stack gap={12} height="100%">
          <ReliabilityMetricsSummary
            metrics={trafficMetrics}
            data={data}
            visibleMetrics={visibleMetrics}
            toggleMetric={toggleMetric}
            testId="workload-reliability-traffic-chart"
            isLoading={isLoading || (hasError && isFetching)}
            formatValue={(summary, metric) => ({
              value: summary,
              unit: summary !== undefined ? metric.unit : undefined,
            })}
          />
          <Box
            position="relative"
            height="100%"
            display="flex"
            flexDirection="column"
            justifyContent="center"
            flex={1}
            minHeight={0}
          >
            <ChartContainer
              data={chartData}
              isLoading={isLoading || (hasError && isFetching)}
              hasNoData={showNoData}
              syncId="workload-reliability"
              analyzeWithOpsPilot={analyzeWithOpsPilot}
            >
              <ChartGrid hideVertical />
              {visibleMetricsList.map((metric) => (
                <Line<ReliabilityDatapoint>
                  key={metric.key}
                  dataKey={metric.key}
                  stroke={metric.stroke}
                  connectNulls={false}
                  isAnimationActive={false}
                  tooltipConfig={{
                    displayName: metric.label,
                    unit: metric.unit,
                  }}
                  dot={
                    <ReliabilitySegmentDot
                      activeDotIndices={pairedGapEdges[metric.key]}
                    />
                  }
                />
              ))}
              <TimeXAxis<ReliabilityDatapoint>
                unixDataKey="chartTimestampUnix"
                timezone={timeZone}
              />
              <YAxis<ReliabilityDatapoint>
                domain={[
                  'dataMin',
                  (dataMax: number) => Math.max(dataMax * 1.2, 1),
                ]}
                allowDecimals
                tickFormatter={trafficTickFormatter}
                unitFormatter={
                  trafficMetrics[0]?.unit
                    ? (formattedValue) =>
                        `${formattedValue} ${trafficMetrics[0].unit}`
                    : undefined
                }
              />
              <ChartTooltip<ReliabilityDatapoint>
                header={(headerProps) => (
                  <ChartTooltipTimestampHeader
                    {...headerProps}
                    timezone={timeZone}
                  />
                )}
                formatValue={(value) => dashesOrNum({ value, roundBy: 2 })}
              />
            </ChartContainer>
          </Box>
        </Stack>
      )}
    </WidgetLayout>
  );
};
